import { Spacing } from '@persona-id/design-tokens';
import { ClipboardIcon } from '@persona-id/design-tokens/icons';
import { ReactNode } from 'react';
import { Field } from 'redux-form';
import styled from 'styled-components';

import InputField from 'dashboard/components/FormControls/InputField';
import copyToClipboard from 'dashboard/lib/utils/copyToClipboard';
import { VerificationTemplateVersion } from 'dashboard/models/verification-template-version';
import Button from 'design-system/components/Button';
import Card from 'design-system/components/Card';
import DropdownMenu from 'design-system/components/DropdownMenu';
import FormControl, { Label } from 'design-system/components/FormControl';
import Input, { InputGroup } from 'design-system/components/Input';
import Popover from 'design-system/components/Popover';
import { Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import { fadeIn } from 'design-system/variables/keyframes';
import { FontWeight } from 'design-system/variables/text';
import { TransitionDuration } from 'design-system/variables/transitions';
import useToggle from 'lib/hooks/useToggle';

const FormWrapper = styled.div`
  display: flex;
  gap: ${Spacing[4]};
`;

const FieldWrapper = styled.div`
  flex: 1;
`;

// TODO (Documents): Migrate to PDS component for underline tooltip indicators when available
const TooltipIndicatorWrapper = styled.div`
  text-decoration: underline;
  text-decoration-style: dotted;
  font-weight: ${FontWeight.Regular};
`;

const CopiedPopover = styled(Card)`
  margin: ${Spacing[1]};
  padding: ${Spacing[2]};
  animation: ${fadeIn} ${TransitionDuration.Medium} ease-in-out;
`;

type DocumentVerificationTemplateHeadSectionProps = {
  vtmplVersion: VerificationTemplateVersion;
  linkedStepCallout?: ReactNode;
  editable: boolean;
};

const DocumentVerificationTemplateHeadSection = ({
  vtmplVersion,
  linkedStepCallout,
  editable,
}: DocumentVerificationTemplateHeadSectionProps) => {
  const vtmplVersionId = vtmplVersion.id;
  const vtmplId = vtmplVersion.relationships['verification-template']?.data?.id;

  const [confirmationActive, { enable: showConfirmation, disable: hideConfirmation }] =
    useToggle(false);

  const copyTextToClipboard = async (text: string) => {
    const copyWasSuccessful = await copyToClipboard(text);
    if (copyWasSuccessful) {
      showConfirmation();
      setTimeout(() => {
        hideConfirmation();
      }, 1200);
    }
  };

  return (
    <Card>
      <Card.Body>
        <Card.Title>General</Card.Title>
        <FormWrapper>
          <FieldWrapper>
            <Field
              type='text'
              name='attributes.name'
              component={InputField}
              disabled={!editable}
              label={<Label>Verification template name</Label>}
              placeholder='Enter verification template name'
            />
          </FieldWrapper>
          <FieldWrapper>
            <Field
              type='text'
              name='attributes.document-kind'
              component={InputField}
              disabled={!editable}
              label={
                <Tooltip
                  side='right'
                  trigger={
                    <TooltipIndicatorWrapper>Document type display label</TooltipIndicatorWrapper>
                  }
                >
                  <Paragraph>
                    Specify a display label that best describes the template use case. This label
                    will be displayed in the verifications overview page.
                  </Paragraph>
                </Tooltip>
              }
              placeholder='Enter document type display label'
            />
          </FieldWrapper>
          <FieldWrapper>
            <FormControl disabled>
              <Label>Verification template ID</Label>
              <InputGroup
                rightButton={
                  vtmplId ? (
                    <>
                      <Popover open={confirmationActive} trigger={<div />}>
                        <Popover.Content side='top'>
                          <CopiedPopover elevated>
                            <Paragraph>Copied</Paragraph>
                          </CopiedPopover>
                        </Popover.Content>
                      </Popover>
                      <DropdownMenu
                        trigger={
                          <Button size='sm' width='icon' variant='inverse'>
                            <ClipboardIcon />
                          </Button>
                        }
                      >
                        <DropdownMenu.Item
                          id='copy-token__verification-template'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyTextToClipboard(vtmplId);
                          }}
                        >
                          Copy Verification Template ID
                        </DropdownMenu.Item>
                        <DropdownMenu.Item
                          id='copy-token__verification-template-version'
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            copyTextToClipboard(vtmplVersionId);
                          }}
                        >
                          Copy Verification Template Version ID
                        </DropdownMenu.Item>
                      </DropdownMenu>
                    </>
                  ) : undefined
                }
              >
                <Input value={vtmplId || 'Unsaved'} />
              </InputGroup>
            </FormControl>
          </FieldWrapper>
        </FormWrapper>
        {linkedStepCallout != null && linkedStepCallout}
      </Card.Body>
    </Card>
  );
};

export default DocumentVerificationTemplateHeadSection;
